import React, { useMemo, Fragment, ReactElement } from 'react';
import { useLocation } from 'react-router';
import LimitedPageContainer from '../../../components/limited-page-block/limited-page-container.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import Controls from '../../app/controls/controls.component';
import FormatService from '../../../services/format-service';
import { DEAL_EXPORT, DEAL_IMPORT } from '../../../constants/entity/permissions';
import { EXPORT_TYPE_LIST_DEAL, EXPORT_TYPE_TAB } from '../../../constants/system/export-types';
import { IMPORT_TYPE_LIST_DEAL, IMPORT_TYPE_TAB } from '../../../constants/system/import-types';
import { IMPORT_EXPORT_ENTITY_TYPE_DEAL } from '../../../constants/system/import-export-entity-types';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { TAB_NAME_SUMMARY } from '../../../constants/system/tab-names';

const DealTab: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const BUTTON_LABEL_PUBLISH: string = 'Publish';

  const {
    isNewDeal,
    isSaving,
    isSaveDisabled,
    isEditMode,
    isExportMode,
    isCustomizable,
    isCustomizeMode,
    isRightDrawerOpen,
    isEmpty,
    isDraft,
    isLimited,
    isTableExport,
    isExportDisabled,
    isGroupExpanded,
    withDelete,
    withGroups,
    withFilters,
    withSelectAll,
    withSelectMode,
    withNoEditMode,
    withStaticEdit,
    withNoMultiLine,
    withAllRowsSelected,
    withAllFiltersEmpty,
    withTabSettings,
    withColumnSettings,
    formError,
    dealId,
    tabSettings,
    tabName,
    tableFields,
    tableFieldGroups,
    tableColumnSettings,
    rowsTotal,
    rowsSelectedTotal,
    selectedRows,
    filters,
    currentFilters,
    createButtonTitle,
    createGroupButtonTitle,
    createAdditionalItemButtonTitle,
    customListOptions,
    customActionMenuItems,
    editPermissions,
    customizePermission,
    importPermission,
    exportPermission,
    changeStatusPermission,
    deletePermission,
    createPermission,
    createGroupPermission,
    createAdditionalItemPermission,
    tabImportFormatList,
    tabExportFormatList,
    onSubmitForm,
    onCancelForm,
    onCreateButtonClick,
    onCreateGroupButtonClick,
    onCreateAdditionalItemButtonClick,
    onExpandAll,
    onCollapseAll,
    onSelectAllRows,
    onTabSettingsChange,
    onTableColumnsChange,
    onExportClick,
    onDeleteClick,
    onExportSuccess,
    onImportSuccess,
    onApplyFilters,
    onResetFilters,
    onStatusChangeClick,
    component,
    filterComponent,
    filterPanelComponent,
  } = props;

  const location: ReactRouterLocation = useLocation();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const isTabVisible: boolean = (tabName === queryTab) || (tabName === TAB_NAME_SUMMARY && (isNewDeal || !queryTab));
  const withNoEditModeCommon: boolean = isNewDeal || withNoEditMode;
  const labelSave: string | null = isDraft ? BUTTON_LABEL_PUBLISH : null;

  const importTypeList: ImportTypeItem[] = useMemo((): ImportTypeItem[] => (
    IMPORT_TYPE_LIST_DEAL.map((importTypeItem: ImportTypeItem): ImportTypeItem => ({
      ...importTypeItem,
      permission: importTypeItem.name === IMPORT_TYPE_TAB ? importPermission : DEAL_IMPORT,
    }))
  ), [importPermission]);
  const exportTypeList: ExportTypeItem[] = useMemo((): ExportTypeItem[] => (
    EXPORT_TYPE_LIST_DEAL.map((exportTypeItem: ExportTypeItem): ExportTypeItem => ({
      ...exportTypeItem,
      permission: exportTypeItem.name === EXPORT_TYPE_TAB ? exportPermission : DEAL_EXPORT,
    }))
  ), [exportPermission]);

  return (
    <Fragment>
      {
        isTabVisible && (
          <Fragment>
            <Controls
              withExportModal
              isSaveDisabled={isSaveDisabled}
              isCustomizable={isCustomizable}
              isCustomizeMode={isCustomizeMode}
              isTableExport={isTableExport}
              isExportDisabled={isExportDisabled}
              isGroupExpanded={isGroupExpanded}
              withDelete={withDelete}
              withGroups={withGroups}
              withFilters={withFilters}
              withStaticEdit={withStaticEdit}
              withSelectAll={withSelectAll}
              withSelectMode={withSelectMode}
              withAllRowsSelected={withAllRowsSelected}
              withNoMultiLine={withNoMultiLine}
              withAllFiltersEmpty={withAllFiltersEmpty}
              withTabSettings={withTabSettings}
              withColumnSettings={withColumnSettings}
              withNoEditMode={withNoEditModeCommon}
              dealId={dealId}
              labelSave={labelSave}
              filters={filters}
              currentFilters={currentFilters}
              customListOptions={customListOptions}
              customActionMenuItems={customActionMenuItems}
              importExportEntityType={IMPORT_EXPORT_ENTITY_TYPE_DEAL}
              tabSettings={tabSettings}
              importFormatList={tabImportFormatList}
              importTypeList={importTypeList}
              importExportTab={tabName}
              exportFormatList={tabExportFormatList}
              exportTypeList={exportTypeList}
              importPermission={importPermission}
              exportPermission={exportPermission}
              changeStatusPermission={changeStatusPermission}
              deletePermission={deletePermission}
              editPermissions={editPermissions}
              customizePermission={customizePermission}
              createPermission={createPermission}
              createGroupPermission={createGroupPermission}
              createAdditionalItemPermission={createAdditionalItemPermission}
              tableFields={tableFields}
              tableFieldGroups={tableFieldGroups}
              rowsTotal={rowsTotal}
              rowsSelectedTotal={rowsSelectedTotal}
              selectedItemIds={selectedRows}
              tableColumnSettings={tableColumnSettings}
              onCancelForm={onCancelForm}
              onSubmitForm={onSubmitForm}
              onExpandAll={onExpandAll}
              onCollapseAll={onCollapseAll}
              onExportClick={onExportClick}
              onDeleteClick={onDeleteClick}
              onApplyFilters={onApplyFilters}
              onResetFilters={onResetFilters}
              onSelectAllRows={onSelectAllRows}
              onImportSuccess={onImportSuccess}
              onExportSuccess={onExportSuccess}
              createButtonTitle={createButtonTitle}
              createGroupButtonTitle={createGroupButtonTitle}
              createAdditionalItemButtonTitle={createAdditionalItemButtonTitle}
              onCreateButtonClick={onCreateButtonClick}
              onCreateGroupButtonClick={onCreateGroupButtonClick}
              onCreateAdditionalItemButtonClick={onCreateAdditionalItemButtonClick}
              onTabSettingsChange={onTabSettingsChange}
              onStatusChangeClick={onStatusChangeClick}
              onTableColumnsChange={onTableColumnsChange}
              filterComponent={filterComponent}
            />
            {filterPanelComponent}
            <LimitedPageContainer isActive={isLimited}>
              <SinglePageTab
                withNoPadding
                withNoBackground
                isEmpty={isEmpty}
                isSaving={isSaving}
                isSaveDisabled={isSaveDisabled}
                isExportDisabled={isExportDisabled}
                isEditMode={isEditMode}
                isExportMode={isExportMode}
                isNewEntity={isNewDeal}
                isRightDrawerOpen={isRightDrawerOpen}
                formError={formError}
                labelSave={labelSave}
                onCancelForm={onCancelForm}
                onSubmitForm={onSubmitForm}
                onExportClick={onExportClick}
              >
                {component}
              </SinglePageTab>
            </LimitedPageContainer>
          </Fragment>
        )
      }
    </Fragment>
  );
});

export default DealTab;
